import React, { useMemo, useRef, useState } from 'react'
import { Head, router, usePage } from '@inertiajs/react'
import { ArrowUp } from 'lucide-react'
import DashboardLayout from '@/frontend/layouts/DashboardLayout'
import { Greeting } from '@/frontend/features/Home/Greeting'
import { AgentPicker } from '@/frontend/features/Home/AgentPicker'
import { HomeDither } from '@/frontend/features/Home/HomeDither'
import { EmptyTeamState } from '@/frontend/features/Home/EmptyTeamState'
import type { HomeWorker } from '@/frontend/features/Home/types'
import { agentChatHref } from '@/frontend/layouts/worker-first/agentAccess'
import { persistSelectedWorker, selectedWorkerStorageContextFromProps } from '@/frontend/layouts/worker-first/useSelectedWorker'
import { threadSuggestionsFor } from '@/frontend/features/AgentChats/assistant-ui/components/threadSuggestions'
import { agentHueStyle } from '@/frontend/lib/agentHue'
import { createSessionOverHttp } from '@/frontend/pages/Dashboard/AgentChats/api'
import type { SharedProps } from '@/frontend/types/inertia'

interface Props {
  page_title: string
  workers: HomeWorker[]
}

const isReady = (worker: HomeWorker) => worker.chat_ready

function unavailableReason(worker: HomeWorker): string {
  if (worker.provisioning_status === 'ready') return 'Chat ainda não disponível'
  if (worker.provisioning_status === 'failed') return 'Instância indisponível'
  return 'Em preparação…'
}

/**
 * Página dedicada de início de conversa (Iteração 3): o passo ANTES da aba
 * Chat — saudação + seletor de agente + composer, na anatomia da home
 * Claude-like da Iteração 1 (coluna central ~620px, muito ar, dither violeta
 * atrás da saudação). O usuário escolhe um agente PRONTO (gate SZ-1583) e
 * escreve o prompt; ao enviar, criamos a conversa por POST e só então
 * navegamos para a aba Chat com `session_id`. O prompt nunca passa pela URL.
 *
 * Renderiza DENTRO do shell da sidebar (DashboardLayout default) — nenhum
 * agente selecionado ainda, então NÃO usa o WorkerPortalShell.
 *
 * Estados: 0 agentes → convite para contratar (EmptyTeamState); agentes só não
 * prontos → convite para aguardar (sem composer que não envia); ≥1 pronto →
 * picker aberto no último usado (prop compartilhado `lastDigitalWorkerId`) →
 * 1º pronto.
 */
function AgentChatsNew({ page_title, workers }: Props) {
  const { props: shared } = usePage<SharedProps>()
  const userName = shared.auth?.user?.name ?? null
  const lastWorkerId = shared.lastDigitalWorkerId ?? null
  const selectionStorageContext = useMemo(
    () => selectedWorkerStorageContextFromProps(shared),
    [shared.auth.user?.id, shared.auth.user?.active_organization?.id],
  )

  const readyWorkers = useMemo(() => workers.filter(isReady), [workers])

  // Alvo padrão: último agente usado (se pronto) → 1º pronto da equipe.
  const defaultTarget =
    readyWorkers.find((w) => w.id === lastWorkerId) || readyWorkers[0] || null
  const [selectedId, setSelectedId] = useState<string | null>(defaultTarget?.id ?? null)
  const target = readyWorkers.find((w) => w.id === selectedId) || defaultTarget

  const [prompt, setPrompt] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  const suggestions = useMemo(
    () => (target ? threadSuggestionsFor(target.job_title).slice(0, 3) : []),
    [target],
  )

  const handleSelect = (id: string) => {
    setSelectedId(id)
    inputRef.current?.focus()
  }

  const prefill = (suggestion: string) => {
    setPrompt(suggestion)
    requestAnimationFrame(() => {
      const el = inputRef.current
      if (!el) return
      el.focus({ preventScroll: true })
      el.setSelectionRange(el.value.length, el.value.length)
    })
  }

  const canSend = !!target && prompt.trim().length > 0 && !submitting

  const submit = async () => {
    if (!target || submitting) return
    const content = prompt.trim()
    if (!content) return

    setSubmitting(true)
    setError(null)
    persistSelectedWorker(target.id, selectionStorageContext)
    try {
      const session = await createSessionOverHttp({
        workerId: target.id,
        content,
        source: 'agent_chats_new',
      })
      router.visit(agentChatHref(target.id, { sessionId: session.id }))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível criar a conversa agora.')
      setSubmitting(false)
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      submit()
    }
  }

  // Sem agente pronto: a página convida a contratar (0 agentes) ou aguardar
  // (equipe existe mas nenhum pronto) — nunca um composer que não envia.
  const hasReady = readyWorkers.length > 0

  return (
    <>
      <Head title={page_title} />
      <div className="mx-auto flex min-h-[70vh] w-full max-w-[620px] animate-fade-up flex-col justify-center gap-8 py-10">
        <div className="relative pt-6">
          <HomeDither />
          <Greeting userName={userName} subtitle="Escolha um agente e escreva o que precisa." />
        </div>

        {workers.length === 0 ? (
          <EmptyTeamState />
        ) : hasReady && target ? (
          <div className="space-y-4">
            <AgentPicker
              workers={workers}
              target={target}
              onSelect={handleSelect}
              isReady={isReady}
              unavailableReason={unavailableReason}
            />

            {/* Composer — mesma anatomia do composer da thread/home (protótipo):
                radius 16, borda forte, [input][enviar 30×30 radius 10 brand];
                foco = borda agent 55% + ring brand 12%. */}
            <div
              data-testid="nova-conversa-composer"
              style={agentHueStyle(target.id)}
              className="flex w-full items-end gap-1.5 rounded-2xl border border-input bg-card px-2 py-2 shadow-xs transition-[border-color,box-shadow] duration-150 focus-within:border-agent/55 focus-within:ring-[3px] focus-within:ring-brand/12"
            >
              <textarea
                ref={inputRef}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={handleKeyDown}
                rows={1}
                autoFocus
                data-testid="nova-conversa-input"
                aria-label={`Mensagem para ${target.name}`}
                placeholder={`Pergunte ou dê uma instrução para ${target.name.trim().split(/\s+/)[0] || target.name}…`}
                className="max-h-40 min-h-[36px] w-full flex-1 resize-none bg-transparent px-1 py-1.5 text-sm outline-none placeholder:text-muted-foreground"
              />
              <button
                type="button"
                onClick={submit}
                disabled={!canSend}
                data-testid="nova-conversa-send"
                aria-label="Enviar"
                className="flex size-[30px] flex-none items-center justify-center rounded-[10px] bg-primary text-primary-foreground transition-transform duration-150 hover:scale-105 disabled:cursor-not-allowed disabled:opacity-35 disabled:hover:scale-100"
              >
                <ArrowUp aria-hidden="true" className="size-4" strokeWidth={2} />
              </button>
            </div>

            {error && (
              <p role="alert" className="text-center text-[12.5px] text-destructive">
                {error}
              </p>
            )}

            {suggestions.length > 0 && (
              <div
                data-testid="nova-conversa-suggestions"
                className="flex flex-wrap justify-center gap-2"
              >
                {suggestions.map((suggestion) => (
                  <button
                    key={suggestion}
                    type="button"
                    data-testid="nova-conversa-suggestion"
                    onClick={() => prefill(suggestion)}
                    className="rounded-full border border-border px-[13px] py-[5px] text-[12.5px] text-muted-foreground transition-colors duration-150 hover:border-input hover:bg-card hover:text-foreground"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            )}

            <p className="text-center text-[11px] text-faint-foreground">
              Enter envia · Shift+Enter quebra linha
            </p>
          </div>
        ) : (
          // Equipe existe, mas nenhum agente está pronto para conversar ainda.
          <div
            data-testid="nova-conversa-nenhum-pronto"
            className="rounded-xl border border-dashed border-border px-6 py-10 text-center"
          >
            <p className="text-sm font-medium text-foreground">
              Nenhum colaborador está pronto para conversar ainda
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              Assim que a instância de um colaborador ficar pronta, a conversa abre por aqui.
            </p>
          </div>
        )}
      </div>
    </>
  )
}

AgentChatsNew.layout = (page: React.ReactElement) => <DashboardLayout>{page}</DashboardLayout>

export default AgentChatsNew
